'use client';

import React, { useMemo } from 'react';
import sanitizeHtml from 'sanitize-html';
import { getExternalLinkRel, isExternalHttpUrl } from '@/lib/utils';

interface BlogContentProps {
  content: string;
}

// Give headings stable, text-derived anchor ids so the in-page table of contents
// and deep links keep working across renders and builds. Author-provided ids are
// left untouched; only headings without an id get one, slugified from their text
// and de-duplicated against every id already present in the document.
function addHeadingIds(html: string): string {
  const used = new Set<string>();
  const existingIdRegex = /\sid\s*=\s*["']([^"']+)["']/gi;
  let idMatch: RegExpExecArray | null;
  while ((idMatch = existingIdRegex.exec(html)) !== null) {
    used.add(idMatch[1]);
  }

  const slugify = (text: string) =>
    text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');

  return html.replace(
    /<(h[1-6])([^>]*)>([\s\S]*?)<\/\1>/gi,
    (match, tag: string, attrs: string, inner: string) => {
      if (/\sid\s*=/.test(attrs)) return match; // preserve author-provided id
      const base = slugify(inner.replace(/<[^>]*>/g, ''));
      if (!base) return match;
      let unique = base;
      let i = 2;
      while (used.has(unique)) unique = `${base}-${i++}`;
      used.add(unique);
      return `<${tag}${attrs} id="${unique}">${inner}</${tag}>`;
    }
  );
}

const BlogContent: React.FC<BlogContentProps> = ({ content }) => {
  const sanitizedContent = useMemo(() => {
    if (!content) return '';

    const sanitized = sanitizeHtml(content, {
      allowedTags: sanitizeHtml.defaults.allowedTags.concat(['img', 'iframe']),
      allowedAttributes: {
        a: ['href', 'title', 'target', 'rel', 'aria-label'],
        img: ['src', 'alt', 'width', 'height', 'loading'],
        iframe: ['src', 'width', 'height', 'allowfullscreen', 'title'],
        h1: ['id'],
        h2: ['id'],
        h3: ['id'],
        h4: ['id'],
        h5: ['id'],
        h6: ['id'],
        div: ['class'],
        span: ['class'],
        p: ['class'],
        ul: ['class'],
        ol: ['class'],
        li: ['class'],
        table: ['class'],
        thead: ['class'],
        tbody: ['class'],
        tr: ['class'],
        th: ['class'],
        td: ['class'],
      },
      transformTags: {
        a: (tagName, attribs: any) => {
          const modified = { ...attribs };
          const href = modified.href || '';
          const isExternal = isExternalHttpUrl(href);
          const isInternalHttpLink = href.startsWith('/') || (/^https?:\/\//i.test(href) && !isExternal);

          if (!modified['aria-label'] && (!modified.title || modified.title.trim() === '')) {
            modified['aria-label'] = 'Link to ' + (modified.href || 'page');
          }

          if (isInternalHttpLink) {
            // Internal link (open in same tab)
            return { tagName: 'a', attribs: { ...modified, target: '_self' } };
          }

          if (isExternal) {
            // External link (open in new tab)
            if (!modified['aria-label']) {
              modified['aria-label'] = `${modified.title || 'External link'} (opens in new tab)`;
            }
            return {
              tagName: 'a',
              attribs: {
                ...modified,
                target: '_blank',
                rel: getExternalLinkRel(href, modified.rel || 'noopener noreferrer'),
              },
            };
          }

          return { tagName: 'a', attribs: modified };
        },
        // The blog page already renders the post title as the single page <h1>, so a
        // content-level <h1> is demoted to <h2>. All lower levels (h2–h6) are left as
        // authored so the section/subsection hierarchy (h2 > h3 > h4 …) is preserved.
        // Anchor ids are added afterwards by addHeadingIds().
        h1: (tagName, attribs) => ({ tagName: 'h2', attribs }),
      },
    });

    return addHeadingIds(sanitized);
  }, [content]);

  return (
    <div
      className="prose max-w-none"
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
    />
  );
};

export default BlogContent;
